import queryString from 'query-string'
import i18n from 'i18next'
import { DefinitionsFromApi, FetchArgs, OverrideResultType } from '@reduxjs/toolkit/query'
import {
  AdvertiserReportsAllAdvertisersItems,
  api,
  GetAdOverviewAdsApiArg,
  GetAdOverviewAffiliatesAdsApiArg,
  GetAdvertiserReportsAllAdvertisersApiArg,
  GetAdvertiserReportsAllAdvertisersApiResponse,
  GetAdvertiserReportsOrganicApiArg,
  GetAffiliateLastAdApiArg,
  GetComplianceNonCompliantAffiliatesApiArg,
  GetExportReportsAffiliateAdsApiArg,
  GetExportReportsAffiliateLastAdApiArg,
  GetExportReportsAllAdvertisersApiArg,
  GetExportReportsDomainAdvertisersApiArg,
  GetExportReportsNonCompliantAffiliatesApiArg,
  GetFilterAdvertiserLandingApiArg,
  GetLanguageComplianceExportApiArg,
  GetLanguageComplianceFilterAffiliateApiArg,
  GetLanguageComplianceFilterPageApiArg,
  GetLanguageComplianceHistoryApiArg,
  GetLanguageComplianceHistoryDetailApiArg,
  GetLanguageComplianceInProgressApiArg,
  GetLanguageComplianceLastCheckApiArg,
  GetResearchAdvertisingAffiliatesApiArg,
  GetResearchAdvertisingApiArg,
  GetResearchAdvertisingKeywordApiArg,
  GetResearchBrandPresenceApiArg,
  GetResearchBrandPresencePagesApiArg,
  GetResearchCompetitorsApiArg,
  GetResearchCompetitorsColumnApiArg,
  GetResearchKeywordsApiArg,
  GetResearchKeywordsColumnAdvertisersApiArg,
  GetResearchSeoPositionApiArg,
  GetTmInfringementsListApiArg,
  PostAdOverviewPublisherApiArg,
  PostResearchCompetitorsChartApiArg,
  PostResearchKeywordsChartApiArg,
  PostResearchSeoPositionChartApiArg,
  PostSiteContactApiArg,
  PostSiteGoogleOauthApiResponse,
  PostSiteLoginApiResponse,
  PostSiteRequestDemoApiArg,
  PostSiteSignupApiArg,
  PostTmInfringementsSubmitApiArg,
  PostTmInfringementsWhitelistApiArg,
  PostTrademarkActivateApiArg,
  PostTrademarkArchiveApiArg,
  PostTrademarkCreateApiArg,
  PostTrademarkRegistrationAddApiArg,
  PostTrademarkRegistrationDeleteApiArg,
  PostTrademarkRegistrationUpdateApiArg,
  PostTrademarkUpdateApiArg,
  PostTrademarkUploadLetterApiArg,
  Success,
} from './endpoints'
import { baseApi } from './base-api'
import { IUserModel } from '../store/models/User.model'
import { login, logOut } from '../store/reducers/AuthSlice'
import { handleCommonResponse } from '../../shared/lib/helpers/ErrorHandlingHelplers'
import { objectToFormData } from '../../shared/lib/helpers/ObjectToFormData'
import { ICommonExport } from '../../shared/types/CommonExport.interface'
import { IOption } from '../../shared/components/forms/search-input/BpSearchInput'
import { mapToOptions } from '../../shared/lib/helpers/MapToOptions'
import { setDemoProject, setMenuLayout } from '../store/reducers/AppSlice'
import { TMenuLayout } from '../../shared/consts/MenuLayout.const'
import {
  chartBody,
  CountryOption,
  dictToOptions,
  downloadFile,
  IChartModel,
  joinIds,
  namesToOptions,
  noCache,
  openFile,
  packUtm,
  ProjectOption,
  queryStringQuery,
  stringsToOptions,
  toAdvertiserNameOptions,
  toCityOptions,
  toCountryOptions,
  toEngineOptions,
  toLandingOptions,
  toLineSeries,
  toProjectOptions,
  urlencoded,
  UtmCookies,
  withExport,
  withSearch,
  withSort,
} from './utils'

export * from './endpoints'
export type { CountryOption, IChartModel, ProjectOption, UtmCookies } from './utils'

type FilterDefinitions = DefinitionsFromApi<typeof api>

export type LandingFilterArg = GetFilterAdvertiserLandingApiArg & { seo?: boolean }

// Списки id уходят строкой через запятую, как в main: openapi объявляет их массивами, но в
// экспортах с queryString массив ушёл бы повторами ключа, из которых PHP берёт последний
export type CommaLists<T> = {
  [K in keyof T]: NonNullable<T[K]> extends (infer E)[] ? T[K] | E | string : T[K]
}

export const enhancedApi = api.enhanceEndpoints<
  never,
  {
    getFilterAdvertiserDomains: OverrideResultType<
      FilterDefinitions['getFilterAdvertiserDomains'],
      IOption[]
    >
    getFilterAdvertiserLanding: OverrideResultType<
      FilterDefinitions['getFilterAdvertiserLanding'],
      IOption[]
    >
    getFilterAdvertiserNames: OverrideResultType<
      FilterDefinitions['getFilterAdvertiserNames'],
      IOption[]
    >
    getFilterAffiliateStatus: OverrideResultType<
      FilterDefinitions['getFilterAffiliateStatus'],
      IOption[]
    >
    getFilterAffiliates: OverrideResultType<FilterDefinitions['getFilterAffiliates'], IOption[]>
    getFilterAssistanceRequest: OverrideResultType<
      FilterDefinitions['getFilterAssistanceRequest'],
      IOption[]
    >
    getFilterBrowser: OverrideResultType<FilterDefinitions['getFilterBrowser'], IOption[]>
    getFilterBrowserLanguage: OverrideResultType<
      FilterDefinitions['getFilterBrowserLanguage'],
      IOption[]
    >
    getFilterCaseId: OverrideResultType<FilterDefinitions['getFilterCaseId'], IOption[]>
    getFilterCity: OverrideResultType<FilterDefinitions['getFilterCity'], IOption[]>
    getFilterCompanyRole: OverrideResultType<FilterDefinitions['getFilterCompanyRole'], IOption[]>
    getFilterContactMethod: OverrideResultType<
      FilterDefinitions['getFilterContactMethod'],
      IOption[]
    >
    getFilterCountry: OverrideResultType<FilterDefinitions['getFilterCountry'], CountryOption[]>
    getFilterDeviceTypes: OverrideResultType<FilterDefinitions['getFilterDeviceTypes'], IOption[]>
    getFilterDevices: OverrideResultType<FilterDefinitions['getFilterDevices'], IOption[]>
    getFilterDomainZones: OverrideResultType<FilterDefinitions['getFilterDomainZones'], IOption[]>
    getFilterEngine: OverrideResultType<FilterDefinitions['getFilterEngine'], IOption[]>
    getFilterEngineLiveSearch: OverrideResultType<
      FilterDefinitions['getFilterEngineLiveSearch'],
      IOption[]
    >
    getFilterKeywords: OverrideResultType<FilterDefinitions['getFilterKeywords'], IOption[]>
    getFilterNetwork: OverrideResultType<FilterDefinitions['getFilterNetwork'], IOption[]>
    getFilterOwnDomains: OverrideResultType<FilterDefinitions['getFilterOwnDomains'], IOption[]>
    getFilterProjects: OverrideResultType<FilterDefinitions['getFilterProjects'], ProjectOption[]>
    getFilterSubRoles: OverrideResultType<FilterDefinitions['getFilterSubRoles'], IOption[]>
    getFilterTmInfringementDomains: OverrideResultType<
      FilterDefinitions['getFilterTmInfringementDomains'],
      IOption[]
    >
    getFilterTrademark: OverrideResultType<FilterDefinitions['getFilterTrademark'], IOption[]>
  }
>({
  endpoints: {
    getAdOverviewAds: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetAdOverviewAdsApiArg) => FetchArgs,
      )
    },
    getAdOverviewAffiliates: { providesTags: ['AdOverviewAffiliate'] },
    getAdOverviewAffiliatesAds: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetAdOverviewAffiliatesAdsApiArg) => FetchArgs,
      )
    },
    getAdOverviewPublisher: { providesTags: ['Publisher'] },
    getAdminVirtualUsers: { providesTags: ['Admin'] },
    getAdvertiserReportsAllAdvertisers: { providesTags: ['SiteSettings', 'AdvertiserReport'] },
    getAdvertiserReportsOrganic: (definition) => {
      definition.providesTags = (_result, _error, arg) =>
        (arg as ExportOrganicResultsArg).export ? [] : ['SiteSettings', 'OrganicReport']

      definition.query = withExport(
        definition.query as (arg: GetAdvertiserReportsOrganicApiArg) => FetchArgs,
      )
    },
    getAffiliateGet: { providesTags: ['Affiliate'] },
    getAffiliateLastAd: (definition) => {
      definition.providesTags = ['Affiliate']

      const query = definition.query as (arg: GetAffiliateLastAdApiArg) => FetchArgs
      definition.query = (queryArg) => {
        const request = query(queryArg)
        const arg = queryArg as AffiliateLastAdArg

        return {
          ...request,
          params: {
            ...request.params,
            browser_id: arg.browserId,
            city_id: arg.cityId,
            engine_id: arg.engineId,
          },
        }
      }
    },
    getAffiliateRuleList: { providesTags: ['AffiliateRule'] },
    getComplianceNonCompliantAffiliates: (definition) => {
      const query = definition.query as (
        arg: GetComplianceNonCompliantAffiliatesApiArg,
      ) => FetchArgs
      definition.query = (queryArg) => {
        const request = query(queryArg)

        return {
          ...request,
          params: {
            ...request.params,
            policy: (queryArg as NonCompliantAffiliatesArg).policy,
            keyword_id: (queryArg as NonCompliantAffiliatesArg).keywordId,
          },
        }
      }
    },
    getDomainTagList: { providesTags: ['DomainTag'] },
    getExportReportsAffiliateAds: {
      query: (queryArg) => {
        const arg = queryArg as ExportAffiliateAdsArg

        return {
          url: `/export-reports/affiliate-ads?${queryString.stringify({
            project_id: arg.projectId,
            country_id: arg.countryId,
            affiliate: arg.affiliate,
            affiliate_id: arg.affiliateId,
            network: arg.network,
            date: arg.date,
            site: arg.site,
            engine_id: arg.engineId,
            browser_id: arg.browserId,
            city_id: arg.cityId,
            is_hidden: arg.isHidden,
            is_hijack: arg.isHijack,
            is_shopping_ads: arg.isShoppingAds,
            status_id: arg.statusId,
            advertiser_id: arg.advertiserId,
            reversed_filters: arg.reversedFilters,
            export: arg.export,
          })}`,
          responseHandler: downloadFile,
          cache: 'no-cache',
        }
      },
    },
    getExportReportsAffiliateLastAd: {
      query: (queryArg) => ({
        url: `/export-reports/affiliate-last-ad?${queryString.stringify({
          id: queryArg.id,
          project_id: queryArg.projectId,
          country_id: queryArg.countryId,
          engine_id: queryArg.engineId,
          city_id: queryArg.cityId,
          browser_id: queryArg.browserId,
          os_id: queryArg.osId,
          site: queryArg.site,
          keyword: queryArg.keyword,
          advertiser_name: queryArg.advertiserName,
          advertiser_id: queryArg.advertiserId,
          reversed_filters: queryArg.reversedFilters,
          date: queryArg.date,
          is_hijack: queryArg.isHijack,
          show_shopping_ads: queryArg.showShoppingAds,
          is_top: queryArg.isTop,
          affiliate: queryArg.affiliate,
        })}`,
        responseHandler: openFile,
        cache: 'no-cache',
      }),
    },
    getExportReportsAllAdvertisers: {
      query: (queryArg) => {
        const arg = queryArg as ExportAllAdvertisersArg

        return {
          url: `/export-reports/all-advertisers?${queryString.stringify({
            page: arg.page,
            'per-page': arg['per-page'],
            sort: arg.sort,
            project_id: arg.projectId,
            country_id: arg.countryId,
            engine_id: arg.engineId,
            browser_id: arg.browserId,
            policy: arg.policy,
            date: arg.date,
            site: arg.site,
            status: arg.status,
            keyword: arg.keyword,
            keyword_id: arg.keywordId,
            affiliate: arg.affiliate,
            affiliate_id: arg.affiliateId,
            advertiser_name: arg.advertiserName,
            advertiser_id: arg.advertiserId,
            show_hidden: arg.showHidden,
            show_shopping_ads: arg.showShoppingAds,
            is_decloak: arg.isDecloak,
            tag_ids: arg.tagIds,
            reversed_filters: arg.reversedFilters,
            export: arg.export,
            'exportColumns[]': arg['exportColumns[]'],
          })}`,
          responseHandler: downloadFile,
          cache: 'no-cache',
        }
      },
    },
    getExportReportsDomainAdvertisers: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetExportReportsDomainAdvertisersApiArg) => FetchArgs,
      )
    },
    getExportReportsNonCompliantAffiliates: {
      query: (queryArg) => {
        const arg = queryArg as ExportNonCompliantAffiliatesArg

        return {
          url: `/export-reports/non-compliant-affiliates?${queryString.stringify({
            project_id: arg.projectId,
            country_id: arg.countryId,
            affiliate: arg.affiliate,
            network: arg.network,
            date: arg.date,
            site: arg.site,
            engine_id: arg.engineId,
            browser_id: arg.browserId,
            city_id: arg.cityId,
            is_hidden: arg.isHidden,
            is_hijack: arg.isHijack,
            show_shopping_ads: arg.showShoppingAds,
            status_id: arg.statusId,
            affiliate_id: arg.affiliateId,
            affiliate_rule_id: arg.affiliateRuleId,
            keyword: arg.keyword,
            advertiser_name: arg.advertiserName,
            advertiser_id: arg.advertiserId,
            reversed_filters: arg.reversedFilters,
            'aff_ids[]': arg.affIds,
            export: arg.export,
            'exportColumns[]': arg['exportColumns[]'],
          })}`,
          responseHandler: downloadFile,
          cache: 'no-cache',
        }
      },
    },
    getFilterAdvertiserDomains: { transformResponse: namesToOptions },
    getFilterAdvertiserLanding: (definition) => {
      definition.transformResponse = toLandingOptions
      definition.query = (queryArg) => ({
        url: '/filter/advertiser-landing',
        params: {
          search: queryArg.search,
          project_id: queryArg.projectId,
          date: queryArg.date,
          seo: (queryArg as LandingFilterArg).seo,
        },
      })
    },
    getFilterAdvertiserNames: { transformResponse: toAdvertiserNameOptions },
    getFilterAffiliateStatus: { transformResponse: dictToOptions },
    getFilterAffiliates: { transformResponse: mapToOptions },
    getFilterAssistanceRequest: { transformResponse: dictToOptions },
    getFilterBrowser: { transformResponse: mapToOptions },
    getFilterBrowserLanguage: { transformResponse: mapToOptions },
    getFilterCaseId: { transformResponse: namesToOptions },
    getFilterCity: { transformResponse: toCityOptions },
    getFilterCompanyRole: { transformResponse: dictToOptions },
    getFilterContactMethod: { transformResponse: dictToOptions },
    getFilterCountry: { providesTags: ['CountriesFilter'], transformResponse: toCountryOptions },
    getFilterDeviceTypes: { transformResponse: mapToOptions },
    getFilterDevices: { transformResponse: mapToOptions },
    getFilterDomainZones: { transformResponse: dictToOptions },
    getFilterEngine: { transformResponse: toEngineOptions },
    getFilterEngineLiveSearch: { transformResponse: mapToOptions },
    getFilterKeywords: { transformResponse: mapToOptions },
    getFilterNetwork: { transformResponse: mapToOptions },
    getFilterOwnDomains: { transformResponse: stringsToOptions },
    getFilterProjects: {
      providesTags: ['ProjectsFilter'],
      transformResponse: toProjectOptions,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        dispatch(setDemoProject(data.find((o) => o.is_demo)))
      },
    },
    getFilterSubRoles: { transformResponse: dictToOptions },
    getFilterTmInfringementDomains: { transformResponse: namesToOptions },
    getFilterTrademark: { transformResponse: mapToOptions },
    getLanguageComplianceCounter: { providesTags: ['LanguageCompliance'] },
    getLanguageComplianceExport: (definition) => {
      const query = definition.query as (arg: GetLanguageComplianceExportApiArg) => FetchArgs
      definition.query = (queryArg) => ({
        ...query(queryArg),
        responseHandler: downloadFile,
        cache: 'no-cache',
      })
    },
    getLanguageComplianceFilterAffiliate: (definition) => {
      definition.query = withSearch(
        definition.query as (arg: GetLanguageComplianceFilterAffiliateApiArg) => FetchArgs,
      )
    },
    getLanguageComplianceFilterPage: (definition) => {
      definition.query = withSearch(
        definition.query as (arg: GetLanguageComplianceFilterPageApiArg) => FetchArgs,
      )
    },
    getLanguageComplianceHistory: (definition) => {
      definition.providesTags = ['LanguageCompliance']
      definition.query = withSort(
        definition.query as (arg: GetLanguageComplianceHistoryApiArg) => FetchArgs,
      )
    },
    getLanguageComplianceHistoryDetail: (definition) => {
      definition.query = withSort(
        definition.query as (arg: GetLanguageComplianceHistoryDetailApiArg) => FetchArgs,
      )
    },
    getLanguageComplianceInProgress: (definition) => {
      definition.providesTags = ['LanguageCompliance']
      definition.query = withSort(
        definition.query as (arg: GetLanguageComplianceInProgressApiArg) => FetchArgs,
      )
    },
    getLanguageComplianceLastCheck: (definition) => {
      definition.providesTags = ['LanguageCompliance']
      definition.query = withSort(
        definition.query as (arg: GetLanguageComplianceLastCheckApiArg) => FetchArgs,
      )
    },
    getNotificationSchedule: { providesTags: ['Notification'] },
    getNotificationState: { providesTags: ['Notification'] },
    getProfileInfo: { providesTags: ['User'] },
    getProjectAvailableCountries: { providesTags: ['Project'] },
    getProjectBrandDomains: { providesTags: ['Project'] },
    getProjectDownloadReport: {
      query: (queryArg) => ({
        url: '/project/download-report',
        params: { project_id: queryArg.projectId },
        responseHandler: async (response) =>
          window.location.assign(window.URL.createObjectURL(await response.blob())),
        cache: 'no-cache',
      }),
    },
    getProjectInfo: { providesTags: ['Project'] },
    getProjectList: { providesTags: ['Project'] },
    getProjectLocations: { providesTags: ['Project'] },
    getProjectLocationsDefaultSettings: {
      providesTags: ['Project'],
      query: (queryArg) => ({
        url: `/project/locations-default-settings?${queryString.stringify({
          'country_ids[]': queryArg.countryIds,
          page: queryArg.page,
          'per-page': queryArg['per-page'],
          sort: queryArg.sort,
        })}`,
      }),
    },
    getProjectMainSettings: { providesTags: ['Project'] },
    getProjectTerms: { providesTags: ['Project'] },
    getReferralBalance: { providesTags: ['Referral'] },
    getReferralConversionStat: { providesTags: ['Referral'] },
    getReferralLinkList: { providesTags: ['Referral'] },
    getReferralPayoutsList: { providesTags: ['Referral'] },
    getReferralStatByMonth: { providesTags: ['Referral'] },
    getReferralTerms: { providesTags: ['Referral'] },
    getReferralUserList: { providesTags: ['Referral'] },
    getResearchAdvertising: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchAdvertisingApiArg) => FetchArgs,
      )
    },
    getResearchAdvertisingAffiliates: (definition) => {
      definition.providesTags = (_result, _error, arg) => (arg.export ? [] : ['ResearchAffiliate'])

      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchAdvertisingAffiliatesApiArg) => FetchArgs,
      )
    },
    getResearchAdvertisingKeyword: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchAdvertisingKeywordApiArg) => FetchArgs,
      )
    },
    getResearchBrandPresence: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchBrandPresenceApiArg) => FetchArgs,
      )
    },
    getResearchBrandPresencePages: (definition) => {
      const query = definition.query as (arg: GetResearchBrandPresencePagesApiArg) => FetchArgs
      definition.query = queryStringQuery((queryArg: BrandPresencePagesArg) => {
        const request = query(queryArg as GetResearchBrandPresencePagesApiArg)

        return {
          ...request,
          params: {
            ...request.params,
            keyword: queryArg.keyword,
            no_serp_presence: queryArg.noSerpPresence,
          },
        }
      })
    },
    getResearchCompetitors: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchCompetitorsApiArg) => FetchArgs,
      )
    },
    getResearchCompetitorsColumn: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchCompetitorsColumnApiArg) => FetchArgs,
      )
    },
    getResearchKeywords: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchKeywordsApiArg) => FetchArgs,
      )
    },
    getResearchKeywordsColumnAdvertisers: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchKeywordsColumnAdvertisersApiArg) => FetchArgs,
      )
    },
    getResearchSeoPosition: (definition) => {
      definition.query = queryStringQuery(
        definition.query as (arg: GetResearchSeoPositionApiArg) => FetchArgs,
      )
    },
    getSiteCurrentuser: {
      providesTags: ['CurrentUser'],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled
          data.data ? dispatch(login(data.data as IUserModel)) : dispatch(logOut())
        } catch (e) {
          dispatch(logOut())
        }
      },
    },
    getSiteCustomerReviews: {
      query: () => ({ url: '/site/customer-reviews', params: { 'per-page': 100 } }),
    },
    getSiteFaq: {
      query: () => ({ url: '/site/faq', params: { 'per-page': 100 } }),
    },
    getTariffList: { providesTags: ['Tariff'] },
    getTeamManagementEditUser: { providesTags: ['Team'] },
    getTeamManagementList: { providesTags: ['Team'] },
    getTmInfringementsList: (definition) => {
      definition.providesTags = (_result, _error, arg) =>
        (arg as ExportTmInfringementsArg).export ? [] : ['TrademarkInfringements']
      definition.query = withExport(
        definition.query as (arg: GetTmInfringementsListApiArg) => FetchArgs,
      )
    },
    getTrademarkCard: { providesTags: ['Trademark'] },
    getTrademarkList: { providesTags: ['Trademark'] },
    getUserCurrentTariffDetails: { providesTags: ['TariffDetails'] },
    postAdOverviewHideAffiliate: { invalidatesTags: ['AdOverviewAffiliate'] },
    postAdOverviewPublisher: {
      invalidatesTags: ['Publisher'],
      query: (queryArg) => ({
        url: '/ad-overview/publisher',
        method: 'POST',
        body: { ...queryArg.body, email: (queryArg as EditPublisherArg).body.email },
      }),
    },
    postAdvertiserReportsDecloakSettings: { invalidatesTags: ['SiteSettings'] },
    postAdvertiserReportsSiteSettings: { invalidatesTags: ['SiteSettings'] },
    postAffiliateCreateNetwork: { invalidatesTags: ['Affiliate', 'Network'] },
    postAffiliateHide: {
      invalidatesTags: ['Affiliate'],
      query: (queryArg) => ({
        url: '/affiliate/hide',
        method: 'POST',
        body: joinIds(queryArg.body),
        cache: 'no-cache',
      }),
    },
    postAffiliateRuleCreate: { invalidatesTags: ['AffiliateRule', 'Project'] },
    postAffiliateRuleDeleteAffiliates: { invalidatesTags: ['AffiliateRule'] },
    postAffiliateRuleRunSync: { invalidatesTags: ['AffiliateRule'] },
    postAffiliateRuleUpdate: {
      invalidatesTags: (_result, _error, arg) =>
        arg.body.scenario === 'update_status'
          ? ['AffiliateRule', 'Project', 'CurrentUser']
          : ['AffiliateRule', 'Project'],
    },
    postAffiliateUpdate: {
      invalidatesTags: (_result, _error, arg) =>
        arg.body.scenario === 'update_network' || arg.body.scenario === 'remove_network'
          ? ['Affiliate', 'Network']
          : ['Affiliate'],
      query: (queryArg) => ({
        url: '/affiliate/update',
        method: 'POST',
        body: joinIds(queryArg.body),
        cache: 'no-cache',
      }),
    },
    postComplianceSendAbuseMail: {
      query: (queryArg) => ({
        url: '/compliance/send-abuse-mail',
        method: 'POST',
        body: objectToFormData(queryArg.body),
        cache: 'no-cache',
      }),
    },
    postDomainTagCreate: { invalidatesTags: ['DomainTag'] },
    postDomainTagDelete: { invalidatesTags: ['DomainTag'] },
    postDomainTagUpdate: { invalidatesTags: ['DomainTag'] },
    postLanguageComplianceUpdateState: {
      invalidatesTags: ['LanguageCompliance'],
      query: (queryArg) => ({
        url: '/language-compliance/update-state',
        method: 'POST',
        body: objectToFormData(queryArg.stateForm),
        cache: 'no-cache',
      }),
    },
    postN8NSendMessage: { invalidatesTags: ['N8n'] },
    postNotificationSaveCard: { invalidatesTags: ['Notification'] },
    postNotificationSaveSchedule: { invalidatesTags: ['Notification'] },
    postProfileChangeEmail: {
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        handleCommonResponse(data, dispatch, 'Email changed')
      },
    },
    postProfileChangePassword: {
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        handleCommonResponse(data, dispatch, 'Password changed')
      },
    },
    postProfileInfo: {
      invalidatesTags: ['User'],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        handleCommonResponse(data, dispatch, 'Account info changed')
      },
    },
    postProfileMenuPosition: {
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled

        handleCommonResponse(data, dispatch)

        if (data.success && data.data) {
          dispatch(setMenuLayout(data.data.front_menu_position as TMenuLayout))
        }
      },
    },
    postProjectCreate: { invalidatesTags: ['CurrentUser', 'Project'] },
    postProjectDeleteDemoProject: { invalidatesTags: ['Project'] },
    postProjectUpdateInfo: { invalidatesTags: ['Project'] },
    postProjectUpdateLocations: { invalidatesTags: ['Project'] },
    postProjectUpdateMainSettings: { invalidatesTags: ['Project'] },
    postProjectUpdateStatus: { invalidatesTags: ['Project'] },
    postProjectUpdateTerms: { invalidatesTags: ['Project', 'CountriesFilter'] },
    postSiteContact: {
      query: (queryArg) => {
        const { body } = queryArg as unknown as ContactArg

        return {
          url: '/site/contact',
          method: 'POST',
          body: urlencoded({
            name: body.name,
            email: body.email,
            company: body.company,
            brand_website: body.website,
            messenger: body.messenger,
            promocode: body.promoCode,
            utm: packUtm(body),
          }),
        }
      },
    },
    postSiteGoogleOauth: {
      transformResponse: (response: PostSiteGoogleOauthApiResponse) =>
        response.success
          ? response
          : {
              ...response,
              errors: {
                login: [
                  'It looks like your account was deleted. If this wasn’t intentional, please reach out to support.',
                ],
              },
            },
    },
    postSiteGoogleReportOauthDeleteToken: {
      query: (queryArg) => ({
        url: '/site/google-report-oauth-delete-token',
        method: 'POST',
        body: urlencoded(queryArg.body),
      }),
    },
    postSiteLogin: {
      query: (queryArg) => ({
        url: '/site/login',
        method: 'POST',
        credentials: 'include',
        body: urlencoded(queryArg.body),
      }),
      transformResponse: (response: PostSiteLoginApiResponse) =>
        response.success
          ? response
          : { ...response, errors: { login: [i18n.t('notifications.incorrectLogin')] } },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        if (data.data) dispatch(login(data.data as IUserModel))
      },
    },
    postSiteLogout: {
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled
        dispatch(logOut())
      },
    },
    postSiteRequestDemo: {
      query: (queryArg) => {
        const { body } = queryArg as unknown as RequestDemoArg

        return {
          url: '/site/request-demo',
          method: 'POST',
          body: urlencoded({
            name: body.name,
            email: body.email,
            company: body.company,
            brand_website: body.website,
            messenger: body.messenger,
            utm: packUtm(body),
          }),
        }
      },
    },
    postSiteRequestPasswordReset: {
      query: (queryArg) => ({
        url: '/site/request-password-reset',
        method: 'POST',
        body: urlencoded(queryArg.body),
      }),
    },
    postSiteResetPassword: {
      query: (queryArg) => ({
        url: '/site/reset-password',
        method: 'POST',
        params: { token: queryArg.token, invite: queryArg.invite },
        body: urlencoded({ ...queryArg.body, invite: queryArg.invite || undefined }),
      }),
    },
    postSiteSignup: {
      query: (queryArg) => {
        const { body } = queryArg as unknown as SignupArg

        return {
          url: '/site/signup',
          method: 'POST',
          body: urlencoded({
            scenario: body.scenario,
            fullName: body.fullName,
            email: body.email,
            ...(body.password
              ? { password: body.password, notifyNews: !!body.notifyNews }
              : { notifyNews: true }),
            ...(body.scenario === 'second' && {
              companyName: body.companyName,
              companyWebsite: body.companyWebsite,
              companyRole: body.companyRole,
              assistanceRequest: body.assistanceRequest,
              contactMethod: body.contactMethod,
              contactDetails: body.contactDetails,
              ...(body.ref_cookie_id && { ref_cookie_id: body.ref_cookie_id }),
            }),
            ...(body.assistanceRequest?.includes('other') && { other: body.other }),
            utm: packUtm(body),
          }),
        }
      },
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled
        if (data.data) dispatch(login(data.data as IUserModel))
      },
    },
    postTeamManagementAddUser: { invalidatesTags: ['Team'] },
    postTeamManagementEditUser: { invalidatesTags: ['Team'] },
    postTeamManagementSendInvite: { invalidatesTags: ['Team'] },
    postTeamManagementUserChangeStatus: { invalidatesTags: ['Team'] },
    postTmInfringementsSubmit: (definition) => {
      definition.invalidatesTags = ['TrademarkInfringements']

      const query = definition.query as (arg: PostTmInfringementsSubmitApiArg) => FetchArgs
      definition.query = noCache((queryArg: PostTmInfringementsSubmitApiArg) => ({
        ...query(queryArg),
        body: { ...queryArg.body, infringement_ids: queryArg.body.infringement_ids.join(',') },
      }))
    },
    postTmInfringementsWhitelist: (definition) => {
      definition.invalidatesTags = ['TrademarkInfringements']
      definition.query = noCache(
        definition.query as (arg: PostTmInfringementsWhitelistApiArg) => FetchArgs,
      )
    },
    postTrademarkActivate: (definition) => {
      definition.invalidatesTags = ['Trademark', 'TrademarkInfringements', 'ProjectsFilter']
      definition.query = noCache(
        definition.query as (arg: PostTrademarkActivateApiArg) => FetchArgs,
      )
    },
    postTrademarkArchive: (definition) => {
      definition.invalidatesTags = ['Trademark', 'TrademarkInfringements', 'ProjectsFilter']
      definition.query = noCache(definition.query as (arg: PostTrademarkArchiveApiArg) => FetchArgs)
    },
    postTrademarkCreate: (definition) => {
      definition.invalidatesTags = ['Trademark']
      definition.query = noCache(definition.query as (arg: PostTrademarkCreateApiArg) => FetchArgs)
    },
    postTrademarkRegistrationAdd: (definition) => {
      definition.invalidatesTags = ['Trademark', 'TrademarkInfringements']
      definition.query = noCache(
        definition.query as (arg: PostTrademarkRegistrationAddApiArg) => FetchArgs,
      )
    },
    postTrademarkRegistrationDelete: (definition) => {
      definition.invalidatesTags = ['Trademark', 'TrademarkInfringements']
      definition.query = noCache(
        definition.query as (arg: PostTrademarkRegistrationDeleteApiArg) => FetchArgs,
      )
    },
    postTrademarkRegistrationUpdate: (definition) => {
      definition.invalidatesTags = ['Trademark', 'TrademarkInfringements']
      definition.query = noCache(
        definition.query as (arg: PostTrademarkRegistrationUpdateApiArg) => FetchArgs,
      )
    },
    postTrademarkUpdate: (definition) => {
      definition.invalidatesTags = ['Trademark']
      definition.query = noCache(definition.query as (arg: PostTrademarkUpdateApiArg) => FetchArgs)
    },
    postTrademarkUploadLetter: (definition) => {
      definition.invalidatesTags = ['Trademark']
      definition.query = noCache(
        definition.query as (arg: PostTrademarkUploadLetterApiArg) => FetchArgs,
      )
    },
    postUserRequestChangeTariff: {
      invalidatesTags: (_result, _error, arg) =>
        arg.requestChangeTariffForm.stripe_precheck ? [] : ['TariffDetails'],
    },
  },
})

export type ExportOrganicResultsArg = CommaLists<
  Partial<Omit<GetAdvertiserReportsOrganicApiArg, 'export' | 'exportColumns'>>
> &
  ICommonExport

export type ExportAdsArg = CommaLists<
  Partial<Omit<GetAdOverviewAdsApiArg, 'export' | 'exportColumns'>>
> &
  ICommonExport

export type ExportAffiliatesAdsArg = CommaLists<
  Partial<Omit<GetAdOverviewAffiliatesAdsApiArg, 'export' | 'exportColumns'>>
> &
  ICommonExport

export type EditPublisherArg = {
  body: PostAdOverviewPublisherApiArg['body'] & { email?: string }
}

export type ExportAllAdvertisersArg = CommaLists<
  Partial<
    Omit<GetAdvertiserReportsAllAdvertisersApiArg, 'export' | 'exportColumns'> &
      Pick<GetExportReportsAllAdvertisersApiArg, 'showAffiliates'>
  >
> &
  ICommonExport

export type ExportAffiliateAdsArg = CommaLists<
  Partial<Omit<GetExportReportsAffiliateAdsApiArg, 'export'>>
> &
  Pick<GetExportReportsAffiliateAdsApiArg, 'export'> & {
    network?: string
    isHidden?: boolean
    isShoppingAds?: boolean
    statusId?: number
    reversedFilters?: string
  }

export type ExportNonCompliantAffiliatesArg = CommaLists<
  Partial<Omit<GetComplianceNonCompliantAffiliatesApiArg, 'export' | 'exportColumns'>>
> &
  ICommonExport

export type AdvertiserReportItem = AdvertiserReportsAllAdvertisersItems & {
  is_affiliate: boolean
}

export type AdvertiserReportsAllAdvertisersResponse = Success & {
  data?: Omit<NonNullable<GetAdvertiserReportsAllAdvertisersApiResponse['data']>, 'items'> & {
    items?: AdvertiserReportItem[]
  }
}

export type SeoPositionChartArg = {
  body: PostResearchSeoPositionChartApiArg['body'] & {
    date_group?: string
    keyword?: string
  }
}

export type AffiliateLastAdArg = CommaLists<GetAffiliateLastAdApiArg> & {
  browserId?: number | string
  cityId?: number | string
  engineId?: number | string
}

export type NonCompliantAffiliatesArg = CommaLists<GetComplianceNonCompliantAffiliatesApiArg> & {
  policy?: string
  keywordId?: string
}

export type LanguageComplianceFilterSearchArg = GetLanguageComplianceFilterPageApiArg & {
  search?: string
}

export type SendVerificationResponse = { data?: { message?: string } }

export type TariffPrecheck = {
  payment_method: {
    type: string
    brand: string
    last4: string
    formatted: string
  }
  currency: string
  base_amount: number
  tax_amount: number
  total_amount: number
  tax_rate_percent: number
}

export type RequestChangeTariffResponse = {
  data?: Partial<TariffPrecheck> & { session_url?: string }
}

export const chartApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getResearchCompetitorsChart: build.query<IChartModel[], PostResearchCompetitorsChartApiArg>({
      query: (queryArg) => ({
        url: '/research/competitors-chart',
        method: 'POST',
        body: chartBody(queryArg.body, ['country_ids', 'project_ids'], []),
      }),
      transformResponse: toLineSeries,
    }),
    getResearchKeywordsChart: build.query<IChartModel[], ResearchKeywordsChartArg>({
      query: (queryArg) => ({
        url: '/research/keywords-chart',
        method: 'POST',
        body: chartBody(queryArg.body, ['country_ids', 'project_ids'], [], ['domain']),
      }),
      transformResponse: toLineSeries,
    }),
    getSiteResetPassword: build.query<Success, { token: string | null; invite: boolean }>({
      query: (queryArg) => ({
        url: '/site/reset-password',
        params: { token: queryArg.token, ...(queryArg.invite && { invite: queryArg.invite }) },
      }),
    }),
    getResearchSeoPositionChart: build.query<IChartModel[], SeoPositionChartArg>({
      query: (queryArg) => ({
        url: '/research/seo-position-chart',
        method: 'POST',
        body: chartBody(queryArg.body, [], ['keyword_ids']),
      }),
      transformResponse: toLineSeries,
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetResearchCompetitorsChartQuery,
  useGetResearchKeywordsChartQuery,
  useGetResearchSeoPositionChartQuery,
  useGetSiteResetPasswordQuery,
} = chartApi

export const useLazyGetDecloakLiveQuery = enhancedApi.endpoints.getDecloakLive.useLazyQuery
export const useLazyGetDomainTagByDomainQuery =
  enhancedApi.endpoints.getDomainTagByDomain.useLazyQuery
export const useLazyGetProjectDownloadReportQuery =
  enhancedApi.endpoints.getProjectDownloadReport.useLazyQuery
export const useLazyGetProjectLocationsDefaultSettingsQuery =
  enhancedApi.endpoints.getProjectLocationsDefaultSettings.useLazyQuery
export const useLazyGetSiteGoogleReportOauthUrlQuery =
  enhancedApi.endpoints.getSiteGoogleReportOauthUrl.useLazyQuery
export type ResearchExportArg<A> = CommaLists<Partial<Omit<A, 'export' | 'exportColumns'>>> &
  ICommonExport & { 'country_ids[]'?: string[] }

export type ContactArg = {
  body: UtmCookies & {
    name?: string
    email?: string
    company?: string
    website?: string
    messenger?: string
    promoCode?: string
    reCaptcha?: string
  }
}

export type RequestDemoArg = {
  body: UtmCookies & {
    name?: string
    email?: string
    company?: string
    website?: string
    messenger?: string
  }
}

export type ExportDomainAdvertisersArg = CommaLists<
  Partial<Omit<GetExportReportsDomainAdvertisersApiArg, 'export' | 'exportColumns'>>
> &
  ICommonExport

export type ExportTmInfringementsArg = CommaLists<
  Partial<Omit<GetTmInfringementsListApiArg, 'export' | 'exportColumns'>>
> &
  ICommonExport

export type SignupArg = {
  body: UtmCookies & PostSiteSignupApiArg['body']
}

export type GoogleOauthResponse = PostSiteGoogleOauthApiResponse & { data?: IUserModel }

export type ResearchAdvertisingKeywordArg = CommaLists<GetResearchAdvertisingKeywordApiArg>

export type BrandPresencePagesArg = CommaLists<GetResearchBrandPresencePagesApiArg> & {
  keyword?: string
  noSerpPresence?: boolean
}

export type ResearchAdvertisingArg = CommaLists<GetResearchAdvertisingApiArg>

export type ResearchKeywordsChartArg = {
  body: PostResearchKeywordsChartApiArg['body'] & { domain?: string[] }
}

export type ResearchTableArg<A> = CommaLists<A> & { 'country_ids[]'?: string[] }

export type ResearchColumnArg<A> = Partial<A> & {
  'country_id[]'?: string[]
  'domain[]'?: string[]
}

const useResearchExport =
  <A>(endpoint: {
    useLazyQuery: () => readonly [(arg: A) => unknown, { isFetching: boolean }, ...unknown[]]
  }) =>
  () => {
    const [trigger, state] = endpoint.useLazyQuery()

    return [(arg: ResearchExportArg<A>) => trigger(arg as A), state] as const
  }

export const useExportCompetitors = useResearchExport(enhancedApi.endpoints.getResearchCompetitors)
export const useExportKeywords = useResearchExport(enhancedApi.endpoints.getResearchKeywords)
export const useExportResearchAds = useResearchExport(
  enhancedApi.endpoints.getResearchCompetitorsColumn,
)
export const useExportAdvertisers = useResearchExport(
  enhancedApi.endpoints.getResearchKeywordsColumnAdvertisers,
)
export const useExportAdvertising = useResearchExport(enhancedApi.endpoints.getResearchAdvertising)
export const useExportAdvertisingAffiliates = useResearchExport(
  enhancedApi.endpoints.getResearchAdvertisingAffiliates,
)
export const useExportSeoPosition = useResearchExport(enhancedApi.endpoints.getResearchSeoPosition)
export const useExportBrandPresence = useResearchExport(
  enhancedApi.endpoints.getResearchBrandPresence,
)

export const useLazyGetResearchLiveDecloakAutoQuery =
  enhancedApi.endpoints.getResearchLiveDecloakAuto.useLazyQuery
export const useLazyGetResearchLiveQuery = enhancedApi.endpoints.getResearchLive.useLazyQuery
export const useLazyGetResearchLiveRedirectsQuery =
  enhancedApi.endpoints.getResearchLiveRedirects.useLazyQuery

export const useExportOrganicResults = () => {
  const [trigger, state] = enhancedApi.endpoints.getAdvertiserReportsOrganic.useLazyQuery()

  return [
    (arg: ExportOrganicResultsArg) => trigger(arg as GetAdvertiserReportsOrganicApiArg),
    state,
  ] as const
}

export const useExportLanguageComplianceHistory = () => {
  const [trigger, state] = enhancedApi.endpoints.getLanguageComplianceExport.useLazyQuery()

  return [
    (arg: CommaLists<GetLanguageComplianceExportApiArg>) =>
      trigger(arg as GetLanguageComplianceExportApiArg),
    state,
  ] as const
}

export const useExportAds = () => {
  const [trigger, state] = enhancedApi.endpoints.getAdOverviewAds.useLazyQuery()

  return [(arg: ExportAdsArg) => trigger(arg as GetAdOverviewAdsApiArg), state] as const
}

export const useExportAffiliatesAds = () => {
  const [trigger, state] = enhancedApi.endpoints.getAdOverviewAffiliatesAds.useLazyQuery()

  return [
    (arg: ExportAffiliatesAdsArg) => trigger(arg as GetAdOverviewAffiliatesAdsApiArg),
    state,
  ] as const
}

export const useExportAllAdvertisers = () => {
  const [trigger, state] = enhancedApi.endpoints.getExportReportsAllAdvertisers.useLazyQuery()

  return [
    (arg: ExportAllAdvertisersArg) => trigger(arg as GetExportReportsAllAdvertisersApiArg),
    state,
  ] as const
}

export const useExportAffiliateAds = () => {
  const [trigger, state] = enhancedApi.endpoints.getExportReportsAffiliateAds.useLazyQuery()

  return [
    (arg: ExportAffiliateAdsArg) => trigger(arg as GetExportReportsAffiliateAdsApiArg),
    state,
  ] as const
}

export const useExportLastAd = () => {
  const [trigger, state] = enhancedApi.endpoints.getExportReportsAffiliateLastAd.useLazyQuery()

  return [
    (arg: CommaLists<GetExportReportsAffiliateLastAdApiArg>) =>
      trigger(arg as GetExportReportsAffiliateLastAdApiArg),
    state,
  ] as const
}

export const useExportNonCompliantAffiliates = () => {
  const [trigger, state] =
    enhancedApi.endpoints.getExportReportsNonCompliantAffiliates.useLazyQuery()

  return [
    (arg: ExportNonCompliantAffiliatesArg) =>
      trigger(arg as GetExportReportsNonCompliantAffiliatesApiArg),
    state,
  ] as const
}

export const useSignupMutation = () => {
  const [trigger, state] = enhancedApi.endpoints.postSiteSignup.useMutation()

  return [(arg: SignupArg) => trigger(arg as unknown as PostSiteSignupApiArg), state] as const
}

export const useRequestDemoMutation = () => {
  const [trigger, state] = enhancedApi.endpoints.postSiteRequestDemo.useMutation()

  return [
    (arg: RequestDemoArg) => trigger(arg as unknown as PostSiteRequestDemoApiArg),
    state,
  ] as const
}

export const useExportDomainAdvertisers = () => {
  const [trigger, state] = enhancedApi.endpoints.getExportReportsDomainAdvertisers.useLazyQuery()

  return [
    (arg: ExportDomainAdvertisersArg) => trigger(arg as GetExportReportsDomainAdvertisersApiArg),
    state,
  ] as const
}

export const useExportTmInfringements = () => {
  const [trigger, state] = enhancedApi.endpoints.getTmInfringementsList.useLazyQuery()

  return [
    (arg: ExportTmInfringementsArg) => trigger(arg as GetTmInfringementsListApiArg),
    state,
  ] as const
}

export const useContactMutation = () => {
  const [trigger, state] = enhancedApi.endpoints.postSiteContact.useMutation()

  return [(arg: ContactArg) => trigger(arg as unknown as PostSiteContactApiArg), state] as const
}

export const useGetAffiliateLastAdQuery = (
  arg: AffiliateLastAdArg,
  options?: Parameters<typeof enhancedApi.endpoints.getAffiliateLastAd.useQuery>[1],
) => enhancedApi.endpoints.getAffiliateLastAd.useQuery(arg as GetAffiliateLastAdApiArg, options)

export const useGetNonCompliantAffiliatesQuery = (
  arg: NonCompliantAffiliatesArg,
  options?: Parameters<
    typeof enhancedApi.endpoints.getComplianceNonCompliantAffiliates.useQuery
  >[1],
) =>
  enhancedApi.endpoints.getComplianceNonCompliantAffiliates.useQuery(
    arg as GetComplianceNonCompliantAffiliatesApiArg,
    options,
  )

export const useGetFilterAdvertiserDomainsQuery =
  enhancedApi.endpoints.getFilterAdvertiserDomains.useQuery
export const useGetFilterAdvertiserNamesQuery =
  enhancedApi.endpoints.getFilterAdvertiserNames.useQuery
export const useGetFilterAffiliateStatusQuery =
  enhancedApi.endpoints.getFilterAffiliateStatus.useQuery
export const useGetFilterAffiliatesQuery = enhancedApi.endpoints.getFilterAffiliates.useQuery
export const useGetFilterAssistanceRequestQuery =
  enhancedApi.endpoints.getFilterAssistanceRequest.useQuery
export const useGetFilterBrowserLanguageQuery =
  enhancedApi.endpoints.getFilterBrowserLanguage.useQuery
export const useGetFilterBrowserQuery = enhancedApi.endpoints.getFilterBrowser.useQuery
export const useGetFilterCaseIdQuery = enhancedApi.endpoints.getFilterCaseId.useQuery
export const useGetFilterCityQuery = enhancedApi.endpoints.getFilterCity.useQuery
export const useGetFilterCompanyRoleQuery = enhancedApi.endpoints.getFilterCompanyRole.useQuery
export const useGetFilterContactMethodQuery = enhancedApi.endpoints.getFilterContactMethod.useQuery
export const useGetFilterDeviceTypesQuery = enhancedApi.endpoints.getFilterDeviceTypes.useQuery
export const useGetFilterDevicesQuery = enhancedApi.endpoints.getFilterDevices.useQuery
export const useGetFilterDomainZonesQuery = enhancedApi.endpoints.getFilterDomainZones.useQuery
export const useGetFilterEngineLiveSearchQuery =
  enhancedApi.endpoints.getFilterEngineLiveSearch.useQuery
export const useGetFilterEngineQuery = enhancedApi.endpoints.getFilterEngine.useQuery
export const useGetFilterKeywordsQuery = enhancedApi.endpoints.getFilterKeywords.useQuery
export const useGetFilterNetworkQuery = enhancedApi.endpoints.getFilterNetwork.useQuery
export const useGetFilterOwnDomainsQuery = enhancedApi.endpoints.getFilterOwnDomains.useQuery
export const useGetFilterProjectsQuery = enhancedApi.endpoints.getFilterProjects.useQuery
export const useGetFilterSubRolesQuery = enhancedApi.endpoints.getFilterSubRoles.useQuery
export const useGetFilterTmInfringementDomainsQuery =
  enhancedApi.endpoints.getFilterTmInfringementDomains.useQuery
export const useGetFilterTrademarkQuery = enhancedApi.endpoints.getFilterTrademark.useQuery

export const useGetFilterAdvertiserLandingQuery =
  enhancedApi.endpoints.getFilterAdvertiserLanding.useQuery
export const useGetFilterCountryQuery = enhancedApi.endpoints.getFilterCountry.useQuery

export default enhancedApi
